import React from "react";
import { Box, Paper, Typography, useTheme, Stack, alpha } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useConstants } from "../../../Main/useConstants";
import { EthiquinEmptyState } from "../../../components/EthiquinEmptyState";

/** Interview summary */
export const Interview = ({
  interview,
  objective,
  interviewed,
  interviewer,
  witness,
}) => {
  const theme = useTheme();
  const [t] = useTranslation("investigation");
  const {
    enums,
    interviews: { rolesOpts },
    functions: { getUsersWitness },
  } = useConstants();

  const isComplainant =
    typeof interviewed === "object" &&
    interviewed?.from === enums.typeUser.COMPLAINANT;

  const titleText = isComplainant
    ? t("Interview.record_of_interview_with_complainant")
    : `${t("Interview.record_of_interview_with")} ${getUsersWitness(
        interviewed
      )}`;

  return (
    <Paper
      variant="outlined"
      sx={{
        p: { xs: 2, sm: 4, md: 5 },
        borderRadius: 2,
        mx: "auto",
        width: "100%",
        maxWidth: 800,
      }}
    >
      <Box>
        <Typography variant="h5" textAlign="center">
          {titleText}
        </Typography>
        <Box
          sx={{
            width: "60%",
            height: "2px",
            bgcolor: "grey.400",
            mx: "auto",
            my: 1,
            borderRadius: 1,
          }}
        />
        <Box component="ul" mt={1.5} sx={{ listStyleType: "disc", pl: 2 }}>
          <Typography component="li">
            <Typography component="span" fontWeight={600}>
              {`${t("Interview.interviewer")}: `}
            </Typography>
            {`${getUsersWitness(interviewer)}`}
          </Typography>

          {witness.map((participant, i) => (
            <Typography component="li" key={i}>
              <Typography component="span" fontWeight={600}>
                {`${
                  rolesOpts.find((opt) => opt.value === participant.role)?.label
                }: `}
              </Typography>
              {`${getUsersWitness(participant)}`}
            </Typography>
          ))}
        </Box>
      </Box>

      {/* Interview Objective */}
      {objective.trim() !== "" && (
        <Box py={0.5}>
          <Typography color="primary" variant="h5">
            {t("Interview.interview_objective")}
          </Typography>
          <Typography
            textAlign="justify"
            variant="body1"
            my={0.5}
            pb={1}
            sx={{ textIndent: "1.5em", whiteSpace: "pre-wrap" }}
            lineHeight={1.4}
            fontSize={14}
          >
            {objective}
          </Typography>
        </Box>
      )}
      {/* Title */}
      <Box sx={{ borderTop: `2px solid ${theme.palette.blueGray200.main}` }}>
        <Typography color="primary" variant="h5" py={1} mt={0.5}>
          {t("Interview.interview")}
        </Typography>
        {interview.length === 0 && (
          <EthiquinEmptyState
            title={t("Interview.interview_not_configured_yet")}
            height="40vh"
            imageWidth={{ xs: "55%", md: 240 }}
          />
        )}
        {interview.map((item, index) => (
          <Stack
            key={index}
            spacing={1}
            sx={{
              mb: 2,
              pb: 2,
              borderBottom: `1px solid ${
                index < interview.length - 1 && theme.palette.blueGray100.main
              }`,
            }}
          >
            {/* # Question */}
            <Typography variant="body1" fontWeight={600}>
              {`${index + 1}. ${item.question}`}
            </Typography>
            {/* Question Objective */}
            {item?.details && (
              <Typography
                variant="body2"
                lineHeight={1.4}
                textAlign="justify"
                sx={{ color: "text.secondary", whiteSpace: "pre-wrap" }}
                fontSize={13}
              >
                {`${t("Interview.objective")}: ${item.details}`}
              </Typography>
            )}
            {/* Interview answer */}
            {item?.answer ? (
              <Typography
                lineHeight={1.4}
                sx={{
                  py: 0.75,
                  px: 1,
                  borderRadius: 2,
                  whiteSpace: "pre-wrap",
                  background: alpha(theme.palette.blueGray50.main, 0.75),
                }}
                textAlign="justify"
                fontStyle="italic"
                variant="body2"
                fontSize={13}
              >
                —"{item.answer}"
              </Typography>
            ) : (
              <Typography
                variant="subtitle2"
                sx={{ fontStyle: "italic", color: "text.disabled" }}
              >
                {t("Interview.no_response_recorded")}
              </Typography>
            )}
            {/* Aditional question details */}
            {item?.answerDetails && item?.answerDetails?.trim() !== "" && (
              <Typography
                variant="body2"
                lineHeight={1.4}
                textAlign="justify"
                fontSize={13}
                sx={{
                  mt: 1,
                  borderLeft: "4px solid",
                  borderColor: "grey.200",
                  display: "block",
                  p: 0.5,
                  borderRadius: 1,
                }}
              >
                <Typography fontWeight={600} component="span" fontSize={13}>
                  {t("evaluation.additional_details")}:
                </Typography>{" "}
                {item.answerDetails}
              </Typography>
            )}
          </Stack>
        ))}
      </Box>
    </Paper>
  );
};
